import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withApiContext, withUserContext } from "context";
import { subscribeDirectChat, emitDirectChatTyping } from "services/chatSocket";
import css from "./directChat.module.scss";

/* ─────────────────────────────────────────────────────────────
   Helpers
   ───────────────────────────────────────────────────────────── */
function newClientMessageId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return null;
}

/** Deterministic pastel color from a string */
const AVATAR_COLORS = [
  ["#5b5ef4", "#8b5cf6"],
  ["#0ea5e9", "#38bdf8"],
  ["#f97316", "#fb923c"],
  ["#10b981", "#34d399"],
  ["#ec4899", "#f472b6"],
  ["#8b5cf6", "#a78bfa"],
];

function avatarColors(name = "") {
  const idx = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function initials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase();
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400_000) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (diff < 86400_000 * 2) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

/* ─────────────────────────────────────────────────────────────
   Sub-components
   ───────────────────────────────────────────────────────────── */

/** Avatar: shows photo if available, otherwise initials gradient */
function ChatAvatar({ name = "", photo = null, small = false }) {
  const [imgError, setImgError] = React.useState(false);
  const [c1, c2] = avatarColors(name);
  const cls = `${css.avatar} ${small ? css.avatarSm : ""}`;

  if (photo && !imgError) {
    return (
      <img
        src={photo}
        alt={initials(name)}
        className={`${cls} ${css.avatarImg}`}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div
      className={cls}
      style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
  );
}

/** CSS-only spinner */
function ChatSpinner() {
  return (
    <div className={css.spinnerWrap} aria-label="Loading…">
      <div className={css.spinner} />
    </div>
  );
}

/** A single conversation / contact row */
function PeerRow({ label, sub, active, unread, name, photo, time, onClick }) {
  return (
    <button
      type="button"
      className={`${css.peerRow} ${active ? css.peerRowActive : ""}`}
      onClick={onClick}
    >
      <ChatAvatar name={name || label} photo={photo} />
      <div className={css.peerInfo}>
        <div className={css.peerTop}>
          <div className={css.peerName}>{label}</div>
          {time && <span className={css.peerTime}>{time}</span>}
        </div>
        {sub && <div className={css.peerSub}>{sub}</div>}
      </div>
      {unread > 0 && <span className={css.unreadBadge}>{unread}</span>}
    </button>
  );
}

/** Send icon (inline SVG — no dep) */
function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className={css.searchIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
    </svg>
  );
}

function AttachIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" />
    </svg>
  );
}

function EmojiIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
    </svg>
  );
}

/** Chat bubble icon for the empty state */
function BubbleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="32" height="32">
      <path
        d="M20 2H4C2.9 2 2 2.9 2 4v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
        fill="#5b5ef4"
        opacity="0.85"
      />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
   Main DirectChat component
   ───────────────────────────────────────────────────────────── */
function DirectChat(props) {
  const {
    variant = "page",
    initialPeerMemberId = null,
    studentMemberId: studentMemberIdProp = null,
  } = props;
  const user = props.ctx_user.user;
  const orgId = user?.organizationId;
  const acadYearId = user?.acadYearId;
  const memberType = Number(user?.type);
  const isParent = memberType === 4;
  const studentMemberId =
    studentMemberIdProp != null && Number.isFinite(Number(studentMemberIdProp))
      ? Number(studentMemberIdProp)
      : isParent && user?.dependantId != null && Number.isFinite(Number(user.dependantId))
        ? Number(user.dependantId)
        : null;

  /** Auth for trainer list + first message only (parent → teacher eligibility) */
  const parentAuthExtras = useCallback(() => {
    if (!isParent || !studentMemberId) return {};
    const extras = { studentMemberId };
    if (orgId) extras.campusId = orgId;
    return extras;
  }, [isParent, studentMemberId, orgId]);

  const isTeacher = memberType === 2;

  const [loading, setLoading] = useState(true);
  const [peers, setPeers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [selectedPeerId, setSelectedPeerId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [typingHint, setTypingHint] = useState("");
  const [teacherTab, setTeacherTab] = useState("student");

  const typingTimer = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  // Tracks the conversationId currently being fetched — used to discard stale responses on switch.
  const activeConvRef = useRef(null);
  // Tracks which initialPeerMemberId has already been resolved so that subsequent
  // conversations refreshes (from markRead / loadConversations) do NOT re-trigger
  // the initial selection and flash the user back to the opening peer.
  const resolvedInitialPeerRef = useRef(null);

  /* ── API loaders ── */
  const loadPeers = useCallback(async () => {
    if (isParent && !studentMemberId) {
      setPeers([]);
      return;
    }
    const args = { ...parentAuthExtras() };
    if (isTeacher && acadYearId) args.acadYearId = acadYearId;
    const { rsp } = await props.ctx_api.privateRequest({ cmd: "chat_eligible_peers", args });
    setPeers(Array.isArray(rsp) ? rsp : []);
  }, [props.ctx_api, isParent, isTeacher, studentMemberId, acadYearId, parentAuthExtras]);

  const loadConversations = useCallback(async () => {
    if (!orgId) return;
    const { rsp } = await props.ctx_api.privateRequest({
      cmd: "chat_list_conversations",
      args: { organizationId: orgId },
    });
    setConversations(Array.isArray(rsp) ? rsp : []);
    if (isTeacher && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("directChat:unreadChanged"));
    }
  }, [props.ctx_api, orgId, isTeacher]);

  const loadMessages = useCallback(
    async (conversationId) => {
      if (!conversationId || !orgId) return;
      // Mark this conversation as the active one and clear stale messages immediately
      // so the user never sees the previous conversation's messages flash.
      activeConvRef.current = conversationId;
      setMessages([]);
      const { rsp } = await props.ctx_api.privateRequest({
        cmd: "chat_get_messages",
        args: { conversationId, organizationId: orgId, limit: 80 },
      });
      // Discard the response if the user switched away while this fetch was in flight.
      if (activeConvRef.current !== conversationId) return;
      setMessages(Array.isArray(rsp) ? rsp : []);
    },
    [props.ctx_api, orgId]
  );

  const markRead = useCallback(
    async (conversationId) => {
      if (!conversationId || !orgId) return;
      await props.ctx_api.privateRequest({
        cmd: "chat_mark_read",
        args: { conversationId, organizationId: orgId },
      });
      loadConversations();
    },
    [props.ctx_api, orgId, loadConversations]
  );

  /* ── Initial load ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orgId) { setLoading(false); return; }
      setLoading(true);
      try {
        const tasks = [loadConversations()];
        if (!isParent || studentMemberId) tasks.push(loadPeers());
        await Promise.all(tasks);
      } catch (e) { console.error(e); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [orgId, isParent, studentMemberId, loadPeers, loadConversations]);

  /* ── Resolve initialPeerMemberId — runs once per peer value, not on every conversations refresh ── */
  useEffect(() => {
    if (initialPeerMemberId == null || !Number.isFinite(Number(initialPeerMemberId))) return;
    const pid = Number(initialPeerMemberId);

    // Already resolved for this peer — don't let subsequent loadConversations() calls
    // overwrite whatever conversation the user has manually switched to.
    if (resolvedInitialPeerRef.current === pid) return;

    // Wait until conversations have loaded before committing (first render may have empty list).
    // Once conversations is non-empty (or after a fresh fetch), make the selection exactly once.
    if (conversations.length === 0) return;

    resolvedInitialPeerRef.current = pid; // mark resolved — further updates won't re-enter
    const conv = conversations.find((c) => Number(c.peerMemberId) === pid);
    setSelectedPeerId(pid);
    setSelectedConvId(conv ? conv.conversationId : null);
  }, [initialPeerMemberId, conversations]);

  /* ── Load messages on conv change ── */
  useEffect(() => {
    if (selectedConvId) { loadMessages(selectedConvId); markRead(selectedConvId); }
    else { setMessages([]); }
  }, [selectedConvId, loadMessages, markRead]);

  /* ── Socket subscriptions ── */
  useEffect(() => {
    const unsub = subscribeDirectChat({
      onMessage: (payload) => {
        const cid = payload?.conversationId;
        if (cid && cid === selectedConvId) {
          setMessages((prev) => {
            const m = payload.message;
            if (!m) return prev;
            if (prev.some((x) => Number(x.message_id) === Number(m.messageId))) return prev;
            return [
              ...prev,
              {
                message_id: m.messageId,
                conversation_id: m.conversationId,
                sender_member_id: m.senderMemberId,
                body: m.body,
                read_at: m.readAt,
                created_at: m.createdAt,
                is_mine: Number(m.senderMemberId) === Number(user.id),
              },
            ];
          });
        }
        loadConversations();
      },
      onTyping: (payload) => {
        if (
          payload?.conversationId === selectedConvId &&
          Number(payload.fromMemberId) !== Number(user.id)
        ) {
          setTypingHint("Typing…");
          if (typingTimer.current) clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setTypingHint(""), 1500);
        }
      },
      onRead: () => { loadConversations(); },
    });
    return () => { unsub(); if (typingTimer.current) clearTimeout(typingTimer.current); };
  }, [selectedConvId, user.id, loadConversations]);

  /* ── Auto-scroll to bottom on new messages ── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typingHint]);

  /* ── Event handlers ── */
  const handleSelectPeer = (p, convHint = null) => {
    const pid = p.memberId;
    setSelectedPeerId(pid);
    const existing =
      convHint ||
      conversations.find((c) => Number(c.peerMemberId) === Number(pid));
    setSelectedConvId(existing ? existing.conversationId : null);
  };

  const send = async () => {
    const body = text.trim();
    if (!body || !orgId || !selectedPeerId) return;
    const clientMessageId = newClientMessageId();
    // Use the client's LOCAL time so the DB stores wall-clock time (e.g. IST),
    // not UTC. getTimezoneOffset() gives the UTC offset in minutes; subtracting
    // it shifts the epoch so that toISOString()'s UTC fields equal local time.
    // Result format: "2026-05-15 12:05:28.669" — matches `timestamp without time zone`.
    const now = new Date();
    const createdAt = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .replace("T", " ")
      .replace("Z", "");
    const args = {
      peerMemberId: selectedPeerId,
      organizationId: orgId,
      body,
      createdAt,
      ...parentAuthExtras(),
    };
    if (clientMessageId) args.clientMessageId = clientMessageId;
    if (isTeacher && acadYearId) args.acadYearId = acadYearId;
    setText("");
    textareaRef.current && (textareaRef.current.style.height = "auto");
    try {
      const { rsp } = await props.ctx_api.privateRequest({ cmd: "chat_send_message", args });
      if (rsp?.conversationId) {
        setSelectedConvId(rsp.conversationId);
        await loadMessages(rsp.conversationId);
        await loadConversations();
      }
    } catch (e) { console.error(e); }
  };

  const onTypingChange = (v) => {
    setText(v);
    if (selectedConvId && selectedPeerId) {
      emitDirectChatTyping({ peerMemberId: selectedPeerId, conversationId: selectedConvId });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  /* Auto-grow textarea */
  const handleTextareaInput = (e) => {
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    onTypingChange(el.value);
  };

  /* ── Helpers ── */
  /** Returns { name, photo } for a peer by memberId */
  const peerInfo = (id, conv) => {
    if (conv?.peerName) {
      return {
        name: conv.peerName,
        photo: conv.peerPhoto || null,
      };
    }
    const p = peers.find((x) => Number(x.memberId) === Number(id));
    return {
      name: p?.name || p?.email || `Member ${id}`,
      photo: p?.photo || null,
    };
  };

  const selectedConv = selectedConvId
    ? conversations.find((c) => Number(c.conversationId) === Number(selectedConvId))
    : null;
  const selectedPeer = selectedPeerId
    ? peerInfo(selectedPeerId, selectedConv)
    : null;
  const selectedPeerName = selectedPeer?.name ?? null;
  const selectedPeerPhoto = selectedPeer?.photo ?? null;

  const studentConversations = useMemo(
    () => conversations.filter((c) => c.conversationType !== "parent"),
    [conversations]
  );
  const parentConversations = useMemo(
    () => conversations.filter((c) => c.conversationType === "parent"),
    [conversations]
  );
  const studentTabUnread = useMemo(
    () => studentConversations.reduce((n, c) => n + (c.unreadCount || 0), 0),
    [studentConversations]
  );
  const parentTabUnread = useMemo(
    () => parentConversations.reduce((n, c) => n + (c.unreadCount || 0), 0),
    [parentConversations]
  );

  const handleTeacherTabChange = (tab) => {
    setTeacherTab(tab);
    if (!selectedConvId) return;
    const conv = conversations.find(
      (c) => Number(c.conversationId) === Number(selectedConvId)
    );
    if (!conv) return;
    const isParentConv = conv.conversationType === "parent";
    if ((tab === "student" && isParentConv) || (tab === "parent" && !isParentConv)) {
      setSelectedConvId(null);
      setSelectedPeerId(null);
      setMessages([]);
      activeConvRef.current = null;
    }
  };

  const renderSidebarList = () => {
    const q = search.trim().toLowerCase();

    const renderPeerList = (peerList, convMap, keyPrefix = "") => {
      const withConv = peerList
        .filter((p) => convMap.has(Number(p.memberId)))
        .sort((a, b) => {
          const tA = convMap.get(Number(a.memberId))?.lastMessageAt;
          const tB = convMap.get(Number(b.memberId))?.lastMessageAt;
          return new Date(tB || 0) - new Date(tA || 0);
        });
      const withoutConv = peerList.filter((p) => !convMap.has(Number(p.memberId)));
      const sorted = [...withConv, ...withoutConv];
      const filtered = q
        ? sorted.filter((p) => {
            const conv = convMap.get(Number(p.memberId));
            const info = peerInfo(p.memberId, conv);
            const haystack = [info.name, p.email, conv?.lastMessagePreview]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return haystack.includes(q);
          })
        : sorted;

      return filtered.map((p) => {
        const conv = convMap.get(Number(p.memberId));
        const unreadCount = conv?.unreadCount || 0;
        const sub =
          unreadCount > 0
            ? `${unreadCount} unread`
            : conv?.lastMessagePreview || p.email || "";
        const info = peerInfo(p.memberId, conv);
        const time = conv?.lastMessageAt ? formatTime(conv.lastMessageAt) : "";
        return (
          <PeerRow
            key={`${keyPrefix}${p.memberId}`}
            label={info.name}
            name={info.name}
            photo={info.photo}
            sub={sub}
            time={time}
            active={
              Number(selectedPeerId) === Number(p.memberId) &&
              (!selectedConvId ||
                Number(selectedConvId) === Number(conv?.conversationId))
            }
            unread={unreadCount}
            onClick={() => handleSelectPeer(p, conv)}
          />
        );
      });
    };

    const renderParentConvList = (parentConvs) => {
      const sorted = [...parentConvs].sort(
        (a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0)
      );
      const filtered = q
        ? sorted.filter((conv) => {
            const info = peerInfo(conv.peerMemberId, conv);
            const haystack = [info.name, conv.peerEmail, conv.lastMessagePreview]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return haystack.includes(q);
          })
        : sorted;

      return filtered.map((conv) => {
        const unreadCount = conv.unreadCount || 0;
        const sub =
          unreadCount > 0
            ? `${unreadCount} unread`
            : conv.lastMessagePreview || conv.peerEmail || "";
        const info = peerInfo(conv.peerMemberId, conv);
        const time = conv.lastMessageAt ? formatTime(conv.lastMessageAt) : "";
        return (
          <PeerRow
            key={`parent-${conv.conversationId}`}
            label={info.name}
            name={info.name}
            photo={info.photo}
            sub={sub}
            time={time}
            active={Number(selectedConvId) === Number(conv.conversationId)}
            unread={unreadCount}
            onClick={() => handleSelectPeer({ memberId: conv.peerMemberId }, conv)}
          />
        );
      });
    };

    if (isTeacher) {
      const studentConvMap = new Map(
        studentConversations.map((c) => [Number(c.peerMemberId), c])
      );
      const rows =
        teacherTab === "parent"
          ? renderParentConvList(parentConversations)
          : renderPeerList(peers, studentConvMap, "s-");

      if (rows.length === 0) {
        return (
          <div className={css.emptySidebar}>
            {teacherTab === "parent"
              ? parentConversations.length === 0
                ? "No parent messages yet."
                : "No matches found."
              : peers.length === 0 && studentConversations.length === 0
                ? "No contacts yet."
                : "No matches found."}
          </div>
        );
      }
      return rows;
    }

    const convByPeer = new Map(conversations.map((c) => [Number(c.peerMemberId), c]));
    const rows = renderPeerList(peers, convByPeer);

    if (rows.length === 0) {
      return (
        <div className={css.emptySidebar}>
          {peers.length === 0 && conversations.length === 0
            ? "No contacts yet."
            : "No matches found."}
        </div>
      );
    }
    return rows;
  };

  /* ── Guard: no org ── */
  if (!orgId) {
    return (
      <div className={css.spinnerWrap} style={{ flexDirection: "column", gap: 8 }}>
        <span style={{ fontSize: 14, color: "#7b849a" }}>
          Select an organization / campus to use chat.
        </span>
      </div>
    );
  }

  /* ── Guard: loading ── */
  if (loading) return <ChatSpinner />;

  const wrapClass = variant === "modal"
    ? `${css.wrap} ${css.wrapModal}`
    : css.wrap;

  return (
    <div className={wrapClass}>
      {/* ── Sidebar ── */}
      <aside className={css.sidebar} aria-label="Conversations">
        <div className={css.sidebarHeader}>
          {variant !== "modal" && <p className={css.sidebarTitle}>Messages</p>}
          <div className={css.searchWrap}>
            <SearchIcon />
            <input
              type="search"
              className={css.searchInput}
              placeholder="Search messages"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search conversations"
            />
          </div>
          {isTeacher && (
            <div className={css.teacherTabs} role="tablist" aria-label="Chat audience">
              <button
                type="button"
                role="tab"
                aria-selected={teacherTab === "student"}
                className={`${css.teacherTab} ${teacherTab === "student" ? css.teacherTabActive : ""}`}
                onClick={() => handleTeacherTabChange("student")}
              >
                Student
                {studentTabUnread > 0 && (
                  <span className={css.teacherTabBadge}>{studentTabUnread}</span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={teacherTab === "parent"}
                className={`${css.teacherTab} ${teacherTab === "parent" ? css.teacherTabActive : ""}`}
                onClick={() => handleTeacherTabChange("parent")}
              >
                Parent
                {parentTabUnread > 0 && (
                  <span className={css.teacherTabBadge}>{parentTabUnread}</span>
                )}
              </button>
            </div>
          )}
        </div>

        <div className={css.sidebarScroll}>{renderSidebarList()}</div>
      </aside>

      {/* ── Thread ── */}
      <section className={css.thread} aria-label="Chat thread">
        {/* Header */}
        <div className={css.threadHeader}>
          {selectedPeerName ? (
            <>
              <ChatAvatar name={selectedPeerName} photo={selectedPeerPhoto} />
              <div className={css.threadHeaderInfo}>
                <div className={css.threadPeerName}>{selectedPeerName}</div>
                <div className={css.onlineStatus}>
                  <span className={css.onlineDot} aria-hidden="true" />
                  <span>Online</span>
                </div>
              </div>
            </>
          ) : (
            <div className={css.threadPeerName} style={{ color: "#7b849a", fontWeight: 500 }}>
              Select a conversation
            </div>
          )}
        </div>

        {/* Messages */}
        {selectedPeerId ? (
          <>
            <div className={css.messages} role="log" aria-live="polite" aria-label="Messages">
              {messages.map((m) => {
                const mine = m.is_mine || Number(m.sender_member_id) === Number(user.id);
                return (
                  <div
                    key={m.message_id}
                    data-mine={mine ? "true" : "false"}
                    className={`${css.bubbleWrapper} ${mine ? css.bubbleWrapperMine : css.bubbleWrapperTheirs}`}
                  >
                    <div className={`${css.bubbleRow} ${mine ? css.bubbleRowMine : ""}`}>
                      {!mine && (
                        <ChatAvatar name={selectedPeerName || ""} photo={selectedPeerPhoto} small />
                      )}
                      <div className={`${css.bubble} ${mine ? css.bubbleMine : css.bubbleTheirs}`}>
                        {m.body}
                      </div>
                    </div>
                    {m.created_at && (
                      <div className={css.meta}>{formatTime(m.created_at)}</div>
                    )}
                  </div>
                );
              })}

              {/* Typing indicator */}
              {typingHint && (
                <div className={css.typingBar}>
                  <div className={css.typingDots}>
                    <span /><span /><span />
                  </div>
                  <span>{selectedPeerName} is typing</span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div className={css.composer}>
              <div className={css.composerField}>
                <textarea
                  ref={textareaRef}
                  className={css.composerTextarea}
                  placeholder="Type your message…"
                  value={text}
                  rows={1}
                  onInput={handleTextareaInput}
                  onChange={(e) => onTypingChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label="Message input"
                />
                <div className={css.composerActions}>
                  <button type="button" className={css.iconBtn} aria-label="Attach file">
                    <AttachIcon />
                  </button>
                  <button type="button" className={css.iconBtn} aria-label="Emoji">
                    <EmojiIcon />
                  </button>
                </div>
              </div>
              <button
                type="button"
                className={css.sendBtn}
                onClick={send}
                disabled={!selectedPeerId || !text.trim()}
                aria-label="Send message"
              >
                <SendIcon />
              </button>
            </div>
          </>
        ) : (
          <div className={css.emptyThread}>
            <div className={css.emptyThreadIcon}>
              <BubbleIcon />
            </div>
            <p className={css.emptyThreadText}>Select a contact to start chatting</p>
          </div>
        )}
      </section>
    </div>
  );
}

export default withApiContext(withUserContext(DirectChat));
